import s from '@/styles/more-time.module.css';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconArrowDown,
  IconArrowRight,
  IconArrowUpRight,
  IconPlayerPlayFilled,
} from '@tabler/icons-react';
import Image from 'next/image';
import Link from 'next/link';
import { BreadcrumbJsonLd, NextSeo, VideoJsonLd } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import FilmLightbox from '@/components/modules/MoreTime/FilmLightbox';
import {
  FILM,
  PLAYLIST_URL,
  SHORTS_URL,
  STORIES,
  type Story,
} from '@/components/modules/MoreTime/stories';
import PageLink from '@/utils/PageLink';

/** Renders an answer line: `{1}` becomes a citation chip. */
const AnswerText = ({ text }: { text: string }) => (
  <>
    {text.split(/(\{\d+\})/).map((part, i) =>
      /^\{\d+\}$/.test(part) ? (
        <sup key={i} className={s.cite}>
          {part.slice(1, -1)}
        </sup>
      ) : (
        <span key={i}>{part}</span>
      )
    )}
  </>
);

const Answer = ({ lines }: { lines: string[] }) => {
  const prose = lines.filter((l) => !l.startsWith('•'));
  const bullets = lines.filter((l) => l.startsWith('•'));
  return (
    <>
      {prose.map((l) => (
        <p key={l}>
          <AnswerText text={l} />
        </p>
      ))}
      {bullets.length > 0 && (
        <ul>
          {bullets.map((l) => (
            <li key={l}>
              <AnswerText text={l.slice(1)} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

const Chapter = ({
  story,
  active,
  onPlay,
}: {
  story: Story;
  active: boolean;
  onPlay: (story: Story) => void;
}) => {
  const m = story.meanwhile;
  return (
    <section
      id={story.id}
      className={`${s.chapter} ${active ? s.isActive : ''}`}
      aria-labelledby={`${story.id}-title`}
      data-story={story.id}>
      <div className={s.still} aria-hidden="true">
        <Image
          src={story.image}
          alt=""
          fill
          sizes="100vw"
          quality={90}
          style={{ objectPosition: story.focus }}
        />
      </div>
      <div className={s.shade} aria-hidden="true" />
      <div className={s.chapterInner}>
        <div className={s.chapterText}>
          <p className={s.number} aria-hidden="true">
            {story.number}
          </p>
          <p className={s.kicker}>More time.</p>
          <h2 id={`${story.id}-title`} className={s.line}>
            {story.line}
          </h2>
          <p className={s.storyText}>{story.story}</p>
          <button
            type="button"
            className={s.play}
            onClick={() => onPlay(story)}
            aria-label={`Watch the film: ${story.line} (30 seconds)`}>
            <span className={s.playIcon}>
              <IconPlayerPlayFilled size={16} aria-hidden="true" />
            </span>
            Watch the film <span className={s.duration}>0:30</span>
          </button>
        </div>
        <aside className={s.meanwhile} aria-label={`Meanwhile, at ${m.business}`} lang={m.lang}>
          <p className={s.meanwhileLabel}>
            <span className={s.pulse} aria-hidden="true" />
            Meanwhile, Chat.co answered
          </p>
          <div className={s.chat}>
            <div className={s.chatHead}>
              <span className={s.avatar} aria-hidden="true">
                ✦
              </span>
              <span>
                <strong>{m.business}</strong>
                <span>{m.channel}</span>
              </span>
            </div>
            <p className={s.asker}>{m.asker}</p>
            <p className={s.question}>{m.question}</p>
            <div className={s.answer}>
              <Answer lines={m.answer} />
            </div>
            <p className={s.source}>
              <span aria-hidden="true">1</span> {m.source}
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
};

const MoreTimePage = () => {
  const [film, setFilm] = useState<{ id: string; title: string } | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const chapterRefs = useRef<HTMLElement[]>([]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    // Gentle snapping between full-screen chapters, clearing the sticky header.
    const root = document.documentElement.style;
    root.scrollSnapType = 'y proximity';
    root.scrollPaddingTop = '64px';
    return () => {
      root.scrollSnapType = '';
      root.scrollPaddingTop = '';
    };
  }, []);

  // Track the chapter in view for the rail and the reveal of each "meanwhile" card.
  useEffect(() => {
    const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-story]'));
    chapterRefs.current = sections;
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive((entry.target as HTMLElement).dataset.story ?? null);
        });
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    sections.forEach((section) => observer.observe(section));
    const outro = document.getElementById('the-idea');
    const intro = document.getElementById('opening');
    const edge = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(null);
        });
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    [intro, outro].forEach((el) => el && edge.observe(el));
    return () => {
      observer.disconnect();
      edge.disconnect();
    };
  }, []);

  const closeFilm = useCallback(() => setFilm(null), []);

  const go = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Arrow keys / J-K step between chapters while the reel is in view.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (film || !active) return;
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const index = STORIES.findIndex((story) => story.id === active);
      if (event.key === 'ArrowDown' || event.key === 'j') {
        event.preventDefault();
        go(index < STORIES.length - 1 ? STORIES[index + 1].id : 'the-idea');
      }
      if (event.key === 'ArrowUp' || event.key === 'k') {
        event.preventDefault();
        go(index > 0 ? STORIES[index - 1].id : 'opening');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, film, go]);

  const activeStory = STORIES.find((story) => story.id === active);

  return (
    <div className={s.page}>
      <NextSeo
        title="More time. For what matters."
        description="Ten people, ten passions, and an assistant that keeps answering while they are away. Watch the Chat.co film series."
        canonical="https://chat.co/more-time"
        openGraph={{
          type: 'website',
          url: 'https://chat.co/more-time',
          title: 'More time. For what matters. | Chat.co',
          description: 'Ten people, ten passions. Watch the Chat.co film series.',
          images: [
            {
              url: 'https://chat.co/images/og-more-time.png',
              width: 1200,
              height: 630,
              alt: 'More time. For what matters. A Chat.co film series',
            },
          ],
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: 'https://chat.co' },
          { position: 2, name: 'More time', item: 'https://chat.co/more-time' },
        ]}
      />
      <VideoJsonLd
        name="More time. For what matters."
        description="A 60-second film from Chat.co about the moments people get back while their AI assistant keeps answering."
        contentUrl={`https://www.youtube.com/watch?v=${FILM.videoId}`}
        embedUrl={`https://www.youtube.com/embed/${FILM.videoId}`}
        uploadDate="2026-10-03"
        duration="PT1M"
        thumbnailUrls={['https://chat.co/more-time/hero.jpg']}
      />
      <a href="#opening" className={s.skipLink}>
        Skip to content
      </a>
      <Header variant="navy" />

      <main>
        <section id="opening" className={s.opening} aria-labelledby="mt-title">
          <div className={`${s.still} ${s.openingStill}`} aria-hidden="true">
            <Image src={FILM.image} alt="" fill priority sizes="100vw" quality={90} />
          </div>
          <div className={s.openingShade} aria-hidden="true" />
          <div className={s.openingInner}>
            <p className={s.eyebrow}>A Chat.co film series</p>
            <h1 id="mt-title" className={s.title}>
              <span>More time.</span>
              <em>For what matters.</em>
            </h1>
            <p className={s.lede}>
              Ten people. Ten passions. And an assistant that keeps answering while they are away.
            </p>
            <div className={s.openingActions}>
              <button
                type="button"
                className={s.primary}
                aria-label="Watch the 60-second film"
                onClick={() => setFilm({ id: FILM.videoId, title: FILM.title })}>
                <IconPlayerPlayFilled size={17} aria-hidden="true" /> Watch the film
                <span className={s.duration}>1:00</span>
              </button>
              <a href="#music" className={s.ghost}>
                Begin the stories <IconArrowDown size={17} aria-hidden="true" />
              </a>
            </div>
          </div>
          <ol className={s.openingIndex} aria-label="The ten stories">
            {STORIES.map((story) => (
              <li key={story.id}>
                <a href={`#${story.id}`}>
                  <span>{story.number}</span>
                  {story.line.replace('For ', '').replace('.', '')}
                </a>
              </li>
            ))}
          </ol>
        </section>

        <nav
          className={`${s.rail} ${activeStory ? s.railVisible : ''}`}
          aria-label="Stories"
          aria-hidden={!activeStory}>
          <ol>
            {STORIES.map((story) => (
              <li key={story.id}>
                <a
                  href={`#${story.id}`}
                  tabIndex={activeStory ? 0 : -1}
                  aria-current={story.id === active ? 'true' : undefined}
                  className={story.id === active ? s.railActive : ''}>
                  <span className={s.railNumber}>{story.number}</span>
                  <span className={s.railLabel}>{story.line}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {STORIES.map((story) => (
          <Chapter
            key={story.id}
            story={story}
            active={story.id === active}
            onPlay={(st) =>
              setFilm({ id: st.videoId, title: `${st.line} A Chat.co film (30 seconds)` })
            }
          />
        ))}

        <section id="the-idea" className={s.idea} aria-labelledby="idea-title">
          <div className={s.ideaGrid}>
            <div className={s.ideaImage}>
              <Image
                src="/more-time/desk-at-night.jpg"
                alt="A laptop on an empty desk in a dark office, a chat.co conversation open on the screen"
                fill
                sizes="(max-width: 900px) 100vw, 55vw"
              />
            </div>
            <div className={s.ideaText}>
              <p className={s.eyebrow}>The idea</p>
              <h2 id="idea-title">
                The office goes dark.
                <br />
                <em>The answers don’t.</em>
              </h2>
              <p>
                Every film ends at the same desk, after hours. The lights are off, and a customer, a
                client, or a colleague asks a question. Chat.co answers from the business’s own
                documents, with the source attached, so the people in these films can be somewhere
                else.
              </p>
              <p>
                On a website, on WhatsApp, in Spanish, and inside the company: ten conversations,
                ten different businesses, one assistant.
              </p>
            </div>
          </div>
        </section>

        <section className={s.finale} aria-labelledby="finale-title">
          <h2 id="finale-title">
            Visit chat.co and
            <br />
            <em>start saving time.</em>
          </h2>
          <div className={s.finaleActions}>
            <Link href={PageLink.login} className={s.primary}>
              Start your free trial <IconArrowRight size={17} aria-hidden="true" />
            </Link>
            <Link href="/contact-us" className={s.ghost}>
              Talk with our team
            </Link>
          </div>
          <ul className={s.finaleLinks}>
            <li>
              <a href={PLAYLIST_URL} target="_blank" rel="noopener noreferrer">
                All films on YouTube <IconArrowUpRight size={15} aria-hidden="true" />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
            <li>
              <a href={SHORTS_URL} target="_blank" rel="noopener noreferrer">
                Vertical versions <IconArrowUpRight size={15} aria-hidden="true" />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
            <li>
              <Link href={PageLink.press}>
                Press & media kit <IconArrowRight size={15} aria-hidden="true" />
              </Link>
            </li>
          </ul>
        </section>
      </main>
      <Footer />

      <FilmLightbox videoId={film?.id ?? null} title={film?.title ?? ''} onClose={closeFilm} />
    </div>
  );
};

export default MoreTimePage;
